import { z } from 'zod';
import { primaryTheme } from '@xbcb/ui-styles';
import { EudrVerificationStatus } from '@xbcb/work-order-types';

export const eudrLineStatusSchema = z.enum([
  'FAILED',
  'TIMED_OUT',
  'INCOMPLETE',
  'IN_PROGRESS',
  'OPERATOR_OVERRIDE',
  'PASSED',
]);

export type EudrLineStatus = z.infer<typeof eudrLineStatusSchema>;

export const EudrLineStatus = eudrLineStatusSchema.enum;

export const isEudrLineStatus = (value: string): value is EudrLineStatus =>
  eudrLineStatusSchema.safeParse(value).success;

export type EudrBadgeStatus =
  | 'success'
  | 'error'
  | 'warning'
  | 'processing'
  | 'default';

export interface EudrStatusDisplay {
  text: string;
  badgeStatus: EudrBadgeStatus;
  color?: string;
}

export const EUDR_STATUS_DISPLAY: Record<EudrLineStatus, EudrStatusDisplay> = {
  PASSED: { text: 'Passed', badgeStatus: 'success' },
  FAILED: { text: 'Failed', badgeStatus: 'error' },
  TIMED_OUT: { text: 'Timed out', badgeStatus: 'warning' },
  IN_PROGRESS: { text: 'In progress', badgeStatus: 'processing' },
  OPERATOR_OVERRIDE: { text: 'Operator override', badgeStatus: 'success' },
  INCOMPLETE: {
    text: 'Incomplete',
    badgeStatus: 'default',
    color: primaryTheme.palette.neutrals.storm,
  },
};

export interface EudrEntryStatusInput {
  isExempt?: boolean | null;
  ddsReferenceNumber?: string | null;
  ddsVerificationNumber?: string | null;
  exemptionCode?: string | null;
  status?: string | null;
}

export const eudrEntryHasInput = (entry?: EudrEntryStatusInput): boolean =>
  entry?.isExempt
    ? !!entry?.exemptionCode?.trim()
    : !!entry?.ddsReferenceNumber?.trim() ||
      !!entry?.ddsVerificationNumber?.trim();

export const eudrEntryIsSubmittable = (entry?: EudrEntryStatusInput): boolean =>
  entry?.isExempt
    ? !!entry?.exemptionCode?.trim()
    : !!entry?.ddsReferenceNumber?.trim() &&
      !!entry?.ddsVerificationNumber?.trim();

const SATISFIED_STATUSES: string[] = [
  EudrVerificationStatus.VERIFIED,
  EudrVerificationStatus.EXEMPT,
  EudrVerificationStatus.OVERRIDDEN,
];
const hasStatus = (entries: EudrEntryStatusInput[], status: string) =>
  entries.some((entry) => entry.status === status);

const isPending = (entry: EudrEntryStatusInput): boolean =>
  entry.status === EudrVerificationStatus.PENDING;

export const isEudrEntrySatisfied = (entry?: EudrEntryStatusInput): boolean =>
  !!entry?.status && SATISFIED_STATUSES.includes(entry.status);

export interface EudrLineStatusInput {
  entries: EudrEntryStatusInput[];
}

export const getEudrLineStatus = ({
  entries,
}: EudrLineStatusInput): EudrLineStatus => {
  if (hasStatus(entries, EudrVerificationStatus.FAILED))
    return EudrLineStatus.FAILED;
  if (hasStatus(entries, EudrVerificationStatus.TIMEOUT))
    return EudrLineStatus.TIMED_OUT;

  const populated = entries.filter(
    (entry) => eudrEntryHasInput(entry) || isEudrEntrySatisfied(entry),
  );

  const needsInput =
    !populated.length ||
    populated.some((e) => !isEudrEntrySatisfied(e) && !isPending(e));
  if (needsInput) return EudrLineStatus.INCOMPLETE;

  if (populated.some(isPending)) return EudrLineStatus.IN_PROGRESS;

  if (populated.some((e) => e.status === EudrVerificationStatus.OVERRIDDEN)) {
    return EudrLineStatus.OPERATOR_OVERRIDE;
  }

  return EudrLineStatus.PASSED;
};

export const isEudrLineCleared = (input: EudrLineStatusInput): boolean => {
  const status = getEudrLineStatus(input);
  return (
    status === EudrLineStatus.PASSED ||
    status === EudrLineStatus.OPERATOR_OVERRIDE
  );
};
